"use client";

import React, { useState, useEffect } from "react";
import { 
  Users, 
  Ticket, 
  Sparkles, 
  Send, 
  QrCode, 
  Flame, 
  CheckCircle2, 
  AlertCircle, 
  Search, 
  Filter, 
  RotateCcw, 
  Calendar, 
  ShieldCheck, 
  MessageSquare, 
  Info,
  DollarSign,
  TrendingUp,
  X,
  ExternalLink
} from "lucide-react";
import RestaurantQrGeneratorModal from "./RestaurantQrGeneratorModal";
import RestaurantVoucherRedeemModal from "./RestaurantVoucherRedeemModal";

interface RestaurantMarketingTabProps {
  restaurantName?: string;
  onRefreshData?: () => void;
}

export default function RestaurantMarketingTab({
  restaurantName = "The Smoke Co.",
}: RestaurantMarketingTabProps) {
  const [stats, setStats] = useState<any>(null);
  const [guests, setGuests] = useState<any[]>([]);
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  // Modals
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
  const [isBroadcastModalOpen, setIsBroadcastModalOpen] = useState(false);

  // Broadcast simulator state
  const [broadcastTitle, setBroadcastTitle] = useState("Weekend Chef's Degustation & Wine Special");
  const [broadcastText, setBroadcastText] = useState(
    "🍷 *Weekend VIP Degustation Treat!*\n\nBook any table this Friday or Saturday and receive a complimentary glass of imported Pinot Noir with your meal.\n\n🎟️ *VIP Code*: *SMOKE-WINE-VIP*\n👉 Show this message to your server.\n\n_Reply STOP anytime to unsubscribe._"
  );
  const [testPhone, setTestPhone] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState<any | null>(null);

  const fetchMarketingData = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/demos/restaurant/marketing");
      const data = await res.json();
      if (data.success) {
        setStats(data.stats);
        setGuests(data.guests);
        setVouchers(data.vouchers);
      }
    } catch (err) {
      console.error("Failed to load restaurant marketing data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketingData();
  }, []);

  // Filter diners
  const filteredGuests = guests.filter((g) => {
    const matchesSearch =
      g.guest_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      g.phone.includes(searchQuery) ||
      (g.table_number && g.table_number.toLowerCase().includes(searchQuery.toLowerCase()));

    if (statusFilter === "ACTIVE") return matchesSearch && g.status === "active";
    if (statusFilter === "OPTED_OUT") return matchesSearch && g.status === "opted_out";
    return matchesSearch;
  });

  const handleSimulateBroadcast = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setBroadcasting(true);
      setBroadcastResult(null);

      const res = await fetch("/api/demos/restaurant/marketing", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          campaign_title: broadcastTitle,
          message_text: broadcastText,
          test_phone: testPhone || undefined,
        }),
      });

      const data = await res.json();
      setBroadcastResult(data);
    } catch (err: any) {
      console.error("Broadcast simulation error:", err);
    } finally {
      setBroadcasting(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      {/* Top Banner with Platform Risk & Multi-Tenant Gate notice */}
      <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-amber-500/20 text-amber-400 shrink-0 mt-0.5">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs font-bold text-amber-300 uppercase tracking-wide flex items-center gap-2">
              <span>DPDP Act 2023 & Meta Quality Standard</span>
              <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 font-mono">
                100% Opted-In Diners
              </span>
            </div>
            <p className="text-xs text-slate-300 mt-1 max-w-2xl leading-relaxed">
              Diners scanned table QRs with unticked mandatory consent. Meta platform health is preserved by suppressing opted-out contacts and enforcing utility template rules.
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <button
            onClick={() => setIsQrModalOpen(true)}
            className="flex-1 sm:flex-initial py-2 px-3.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs font-semibold flex items-center justify-center gap-1.5 transition border border-slate-700"
          >
            <QrCode className="w-4 h-4 text-amber-400" />
            <span>Table QRs</span>
          </button>

          <button
            onClick={() => setIsRedeemModalOpen(true)}
            className="flex-1 sm:flex-initial py-2 px-3.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold flex items-center justify-center gap-1.5 transition shadow-lg shadow-amber-500/20"
          >
            <Flame className="w-4 h-4" />
            <span>Redeem Voucher</span>
          </button>

          <button
            onClick={() => setIsBroadcastModalOpen(true)}
            className="flex-1 sm:flex-initial py-2 px-3.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold flex items-center justify-center gap-1.5 transition shadow-lg shadow-emerald-500/20"
          >
            <Send className="w-4 h-4" />
            <span>Simulate Blast</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* VIP Diners */}
        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 relative overflow-hidden">
          <div className="flex justify-between items-start">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              VIP Opted-In Diners
            </span>
            <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-400">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-bold text-white mt-2">
            {stats ? stats.activeOptedIn : "..."}
          </div>
          <div className="text-[11px] text-emerald-400 flex items-center gap-1 mt-1">
            <CheckCircle2 className="w-3 h-3" />
            <span>100% verified table scans</span>
          </div>
        </div>

        {/* Voucher Redemptions */}
        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 relative overflow-hidden">
          <div className="flex justify-between items-start">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              Voucher Redemption Rate
            </span>
            <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400">
              <Ticket className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-bold text-amber-400 mt-2">
            {stats ? `${stats.redemptionRatePct}%` : "..."}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            {stats ? `${stats.redeemedVouchers} of ${stats.totalVouchers} redeemed` : "..."}
          </div>
        </div>

        {/* Birthdays this month */}
        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 relative overflow-hidden">
          <div className="flex justify-between items-start">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              Birthdays This Month
            </span>
            <div className="p-1.5 rounded-lg bg-purple-500/10 text-purple-400">
              <Calendar className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-bold text-purple-300 mt-2">
            {stats ? stats.birthdaysThisMonth : "..."}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            Auto-birthday dessert triggers
          </div>
        </div>

        {/* Meta Pass-Through Cost */}
        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 relative overflow-hidden">
          <div className="flex justify-between items-start">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              Est. Meta Fee / Blast
            </span>
            <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-bold text-emerald-400 mt-2">
            {stats ? `₹${stats.estMetaPassThroughCost}` : "..."}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            @ ₹0.85/marketing message
          </div>
        </div>
      </div>

      {/* Guest Directory & Search */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800">
          <div>
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <span>VIP Diners Stream</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 font-mono">
                {filteredGuests.length} Guests
              </span>
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Guests captured via table QR codes with affirmative consent logging.
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search name, phone, table..."
                className="bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-3 py-1.5 text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/60 w-48 sm:w-60"
              />
            </div>

            {/* Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-slate-950 border border-slate-800 rounded-xl px-2.5 py-1.5 text-xs text-slate-300 focus:outline-none focus:border-amber-500/60"
            >
              <option value="ALL">All Status</option>
              <option value="ACTIVE">Consented (Active)</option>
              <option value="OPTED_OUT">Opted Out (STOP)</option>
            </select>

            <button
              onClick={fetchMarketingData}
              className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition"
              title="Refresh Diners"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Table View */}
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-slate-950/60 text-slate-400 font-semibold uppercase tracking-wider border-b border-slate-800">
              <tr>
                <th className="py-3 px-4">Guest Name</th>
                <th className="py-3 px-3">WhatsApp Number</th>
                <th className="py-3 px-3">Table / Zone</th>
                <th className="py-3 px-3">Visits</th>
                <th className="py-3 px-3">Birthday</th>
                <th className="py-3 px-3">DPDP Consent</th>
                <th className="py-3 px-3">Active Voucher</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {filteredGuests.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-slate-500">
                    No diners match your query. Scan a table QR to add live guests.
                  </td>
                </tr>
              ) : (
                filteredGuests.map((g) => {
                  const guestVoucher = vouchers.find((v) => v.guest_id === g.id);

                  return (
                    <tr key={g.id} className="hover:bg-slate-800/40 transition">
                      <td className="py-3 px-4 font-semibold text-white">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-amber-400 text-xs font-bold">
                            {g.guest_name.charAt(0)}
                          </div>
                          <div>
                            <div>{g.guest_name}</div>
                            {g.email && (
                              <div className="text-[10px] text-slate-500 truncate max-w-[140px]">
                                {g.email}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="py-3 px-3 font-mono text-slate-300">
                        +91 {g.phone.slice(0, 5)} {g.phone.slice(5)}
                      </td>

                      <td className="py-3 px-3">
                        <span className="font-mono text-[11px] font-semibold text-amber-300 bg-amber-950/40 px-2 py-0.5 rounded border border-amber-800/30">
                          {g.table_number || "Walk-in"}
                        </span>
                      </td>

                      <td className="py-3 px-3">
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-800 text-slate-200">
                          Visit #{g.visit_count}
                        </span>
                      </td>

                      <td className="py-3 px-3">
                        {g.dob ? (
                          <span className="text-purple-300 font-medium text-[11px]">
                            {g.dob}
                          </span>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>

                      <td className="py-3 px-3">
                        {g.status === "opted_out" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-red-500/20 text-red-400 border border-red-500/30">
                            Opted Out (STOP)
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" title={`IP: ${g.consent_ip} | ${g.consent_timestamp}`}>
                            <ShieldCheck className="w-3 h-3" />
                            <span>Consented</span>
                          </span>
                        )}
                      </td>

                      <td className="py-3 px-3">
                        {guestVoucher ? (
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-xs font-bold text-amber-400">
                              {guestVoucher.voucher_code}
                            </span>
                            {guestVoucher.status === "redeemed" ? (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 font-semibold">
                                Used
                              </span>
                            ) : (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-bold border border-emerald-500/30">
                                Active
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>

                      <td className="py-3 px-4 text-right">
                        {guestVoucher && guestVoucher.status === "active" ? (
                          <button
                            onClick={() => setIsRedeemModalOpen(true)}
                            className="py-1 px-2.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30 text-[11px] font-semibold transition inline-flex items-center gap-1"
                          >
                            <Flame className="w-3 h-3 text-amber-400" />
                            <span>Redeem</span>
                          </button>
                        ) : (
                          <span className="text-[11px] text-slate-500">Processed</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Broadcast Simulator Modal */}
      {isBroadcastModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
          <div className="relative w-full max-w-lg bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl p-6 text-slate-100 max-h-[92vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b border-slate-800">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                  <Send className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-white">Broadcast Campaign Simulator</h2>
                  <p className="text-xs text-slate-400">
                    Schedule or dispatch VIP marketing blasts with transparent Meta fees.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsBroadcastModalOpen(false)}
                className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSimulateBroadcast} className="my-5 space-y-4">
              {/* Campaign Title */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                  Campaign Title
                </label>
                <input
                  type="text"
                  required
                  value={broadcastTitle}
                  onChange={(e) => setBroadcastTitle(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500/60"
                />
              </div>

              {/* Message Composer */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                  WhatsApp Message Copy
                </label>
                <textarea
                  rows={5}
                  required
                  value={broadcastText}
                  onChange={(e) => setBroadcastText(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-slate-200 font-sans focus:outline-none focus:border-emerald-500/60 leading-relaxed"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  💡 Meta marketing templates require an unbundled opt-out statement (*"Reply STOP anytime"*).
                </p>
              </div>

              {/* Cost & Suppression Breakdown Box */}
              <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 text-xs space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400">Total Opted-In Diners:</span>
                  <span className="font-semibold text-white">{stats?.activeOptedIn || 0} Recipients</span>
                </div>
                <div className="flex justify-between text-red-400">
                  <span>Suppressed (Opted Out / STOP):</span>
                  <span>{stats?.optedOut || 0} Blocked</span>
                </div>
                <div className="flex justify-between border-t border-slate-800/80 pt-2 text-emerald-400 font-bold">
                  <span>Estimated Meta Fee (@ ₹0.85/msg):</span>
                  <span>₹{stats?.estMetaPassThroughCost || 0}</span>
                </div>
              </div>

              {/* Test Phone Input */}
              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                  Send Test Preview to Owner Phone (Optional)
                </label>
                <div className="flex">
                  <div className="inline-flex items-center px-3 rounded-l-xl bg-slate-950 border border-r-0 border-slate-800 text-xs text-slate-400 font-semibold">
                    🇮🇳 +91
                  </div>
                  <input
                    type="tel"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="Owner test mobile (e.g. 9880893416)"
                    className="w-full bg-slate-950 border border-slate-800 rounded-r-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500/60"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Sends this actual pitch to your phone so the restaurant owner sees how it arrives.
                </p>
              </div>

              {/* Simulation Result */}
              {broadcastResult && (
                <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs">
                  <div className="font-semibold flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span>{broadcastResult.message}</span>
                  </div>
                  {broadcastResult.testDispatchResult && (
                    <div className="text-[11px] text-emerald-300/80 mt-1">
                      📲 Test message dispatched to your phone!
                    </div>
                  )}
                </div>
              )}

              {/* Submit CTA */}
              <button
                type="submit"
                disabled={broadcasting}
                className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-slate-950 font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 active:scale-[0.98] transition disabled:opacity-50"
              >
                {broadcasting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                    <span>Executing Campaign Simulation...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Launch Weekend VIP Blast (Simulated)</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Modals */}
      <RestaurantQrGeneratorModal
        isOpen={isQrModalOpen}
        onClose={() => setIsQrModalOpen(false)}
        restaurantName={restaurantName}
      />

      <RestaurantVoucherRedeemModal
        isOpen={isRedeemModalOpen}
        onClose={() => setIsRedeemModalOpen(false)}
        onVoucherRedeemed={fetchMarketingData}
      />
    </div>
  );
}
